import type { AdmissionsAssistantFeedbackSubmission } from './types';

export type AdmissionsAssistantFeedbackStatusCopy = {
  label: string;
  toneClass: string;
  detail?: string | null;
};

export const resolveFeedbackStatusCopy = (
  submission: AdmissionsAssistantFeedbackSubmission,
): AdmissionsAssistantFeedbackStatusCopy => {
  if (submission.submissionStatus === 'submitting') {
    return {
      label: 'Submitting feedback...',
      toneClass: 'border-primary/20 bg-primary/5 text-primary',
      detail: null,
    };
  }
  if (submission.submissionStatus === 'accepted') {
    const evidenceRef = submission.feedbackArtifactRef ?? submission.artifactRefs?.[0] ?? null;
    if (submission.feedbackArtifactStatus === 'missing') {
      return {
        label: 'Feedback captured, but the authority artifact is missing.',
        toneClass: 'border-strawberry/20 bg-strawberry/5 text-strawberry',
        detail: `${evidenceRef ? `Expected evidence: ${evidenceRef}. ` : ''}${submission.authorityErrorMessage ?? ''}`.trim() || null,
      };
    }
    if (submission.feedbackArtifactStatus === 'mismatch') {
      return {
        label: 'Feedback captured, but authority verification failed.',
        toneClass: 'border-strawberry/20 bg-strawberry/5 text-strawberry',
        detail: `${evidenceRef ? `Evidence: ${evidenceRef}. ` : ''}${submission.authorityErrorMessage ?? ''}`.trim() || null,
      };
    }

    if (submission.authorityErrorMessage) {
      return {
        label: `Feedback captured${submission.feedbackId ? ` (${submission.feedbackId})` : ''}.`,
        toneClass: 'border-amber-200 bg-amber-50 text-amber-800',
        detail: `${submission.authorityErrorMessage}${evidenceRef ? ` Evidence: ${evidenceRef}.` : ''}`.trim(),
      };
    }

    const learningLabel = submission.learningArtifactBacked === false
      ? 'Learning status unavailable.'
      : submission.learningStatus
        ? `Learning status: ${submission.learningStatus}.`
        : 'Learning status unavailable.';
    const replayMatchLabel = typeof submission.replayMatchRate === 'number'
      ? ` Replay match ${(submission.replayMatchRate * 100).toFixed(0)}%.`
      : '';
    const artifactLabel = evidenceRef
      ? ` Evidence: ${evidenceRef}.`
      : '';
    return {
      label: `Feedback captured${submission.feedbackId ? ` (${submission.feedbackId})` : ''}.`,
      toneClass: submission.learningArtifactBacked === false
        ? 'border-amber-200 bg-amber-50 text-amber-800'
        : 'border-emerald-200 bg-emerald-50 text-emerald-800',
      detail: `${learningLabel}${replayMatchLabel}${artifactLabel}`.trim(),
    };
  }
  if (submission.submissionStatus === 'queued') {
    return {
      label: 'Feedback queued locally; sync pending.',
      toneClass: 'border-amber-200 bg-amber-50 text-amber-800',
      detail: null,
    };
  }
  return {
    label: 'Feedback could not be submitted.',
    toneClass: 'border-strawberry/20 bg-strawberry/5 text-strawberry',
    detail: submission.errorMessage ?? null,
  };
};
